import Image from "next/image";
import { z } from "zod";

import { RichText } from "@/components/rich-text";
import { cn } from "@/lib/utils";
import { customCardSchema } from "@/schemas";

import { AnimationPlayer } from "../animation-player";
import { VideoPlayer } from "./shared/video-player";

export function Vertical({
  className,
  ...props
}: z.infer<typeof customCardSchema> & { className?: string }) {
  const {
    title,
    richTextTitle,
    image,
    video,
    mediaType,
    description,
    bgColor,
    videoAnimation,
    isLightText,
    animation,
    loopAnimation,
  } = props;

  const backgroundColor = bgColor?.value ?? "#FAF9F8";

  return (
    <article
      style={{ backgroundColor }}
      className={cn(
        "rounded-container bg-grey-50 relative mx-auto flex size-full min-h-[400px] max-w-[530px] flex-col justify-center gap-4 overflow-hidden lg:gap-5 xl:min-h-[470px]",
        className
      )}
    >
      <div
        className={cn("flex flex-col justify-between", {
          "p4 relative lg:p-5": mediaType === "video",
          "absolute inset-4 z-20 lg:inset-5": mediaType !== "video",
        })}
      >
        <h3
          className={cn("text-heading-16 lg:text-heading-20", {
            "text-black": !isLightText,
            "text-white": isLightText,
          })}
        >
          {richTextTitle ? <RichText value={richTextTitle} /> : title}
        </h3>
        {description && (
          <div className="h-20">
            <p
              className={cn(
                "text-body line-clamp-3 text-black uppercase",
                isLightText && "text-white"
              )}
            >
              {description}
            </p>
          </div>
        )}
      </div>
      <div
        className={cn(
          "flex h-auto w-full items-center justify-center self-stretch overflow-hidden",
          {
            "my-auto": mediaType === "video",
          }
        )}
      >
        {image?.src && mediaType === "image" && (
          <Image
            className="h-auto w-full overflow-hidden object-cover"
            src={image.src}
            alt={image.alt ?? "Large Card Media"}
            width={image.width ?? 0}
            height={image.height ?? 0}
            sizes="(min-width: 624px) 50vw, 100vw"
          />
        )}
        {video && mediaType === "video" && video?.asset && (
          <VideoPlayer
            className="h-auto w-full object-cover"
            asset={video.asset}
            animation={false}
          />
        )}
        {mediaType === "videoAnimation" && videoAnimation?.asset && (
          <VideoPlayer
            className="h-auto w-full object-cover"
            asset={videoAnimation.asset}
            animation={true}
          />
        )}
        {animation && mediaType === "animation" && (
          <AnimationPlayer animation={animation} loop={loopAnimation} />
        )}
      </div>
    </article>
  );
}
